import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Compact/abbreviated display name for suppliers used in shortcut chips
 * (Orders, Finance, Inventory, Stats Réceptions). Returns the input unchanged
 * when no mapping is defined so the full name still appears in tables, PDFs
 * and everywhere else it matters.
 */
const SUPPLIER_SHORTCUT_ALIASES = {
    "Pro-a-Pro Surgelés": "P-a-P Surgelés",
    "Pro-a-Pro Sec & Frais": "P-a-P Sec & Frais",
};
export function supplierShortcutLabel(name) {
    if (!name) return name;
    return SUPPLIER_SHORTCUT_ALIASES[name] || name;
}

// === Multi-company scoping ===
// Site name → company name matching (kept identical to backend `_get_company`)
export const COMPANY_MATCHERS = {
    'Fuji Express': (name) => (name || '').startsWith('FE '),
    'Concept Lab': (name) => name === 'Concept Lab',
    'My Food': (name) => name === 'My Food',
    'Boucherie': (name) => name === 'Boucherie',
};

// Company display name → flag key on suppliers / industrial_products
export const COMPANY_FLAG_KEYS = {
    'Fuji Express': 'fuji_express',
    'Concept Lab': 'concept_lab',
    'My Food': 'my_food',
    'Boucherie': 'boucherie',
};

/**
 * Derive the set of companies a user can operate on from their allowed_sites.
 * When the user has no restriction (allowed_sites empty/null) → all companies.
 * Returns an array of flag keys, e.g. ['fuji_express', 'concept_lab'].
 */
export function getUserAllowedCompanyFlags(user, sites) {
    if (!user) return Object.values(COMPANY_FLAG_KEYS);
    const allowedSiteIds = user.allowed_sites;
    if (!allowedSiteIds || allowedSiteIds.length === 0) {
        return Object.values(COMPANY_FLAG_KEYS);
    }
    const allowedSiteNames = (sites || [])
        .filter(s => allowedSiteIds.includes(s.id))
        .map(s => s.name);
    const flags = new Set();
    for (const [companyName, matcher] of Object.entries(COMPANY_MATCHERS)) {
        if (allowedSiteNames.some(n => matcher(n))) {
            flags.add(COMPANY_FLAG_KEYS[companyName]);
        }
    }
    // Safety: never lock a user out completely — if allowed_sites doesn't
    // resolve to any known company, fall back to all.
    if (flags.size === 0) return Object.values(COMPANY_FLAG_KEYS);
    return Array.from(flags);
}

/**
 * Filter a list of items (suppliers or industrial_products) by user companies.
 * Items with none of the flags set are visible to all (backward compat).
 */
export function filterByUserCompanies(items, userFlagKeys) {
    if (!items || !userFlagKeys || userFlagKeys.length === 0) return items || [];
    const userSet = new Set(userFlagKeys);
    const allFlags = Object.values(COMPANY_FLAG_KEYS);
    // If user has access to all 4 → no filtering
    if (userSet.size >= allFlags.length) return items;
    return items.filter(it => {
        // Item is visible if at least one of its enabled flags intersects with user's
        const hasAnyFlag = allFlags.some(f => it[f] === true);
        if (!hasAnyFlag) return true; // no flags set → assume visible (legacy)
        return allFlags.some(f => it[f] === true && userSet.has(f));
    });
}



// Stable per-device identifier + practical device info, sent with login for the connection log
export function getDeviceInfo() {
    let deviceId = localStorage.getItem('sokuzai_device_id');
    if (!deviceId) {
        deviceId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem('sokuzai_device_id', deviceId);
    }
    return {
        device_id: deviceId,
        device_name: localStorage.getItem('sokuzai_device_name') || null,
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        touch: (navigator.maxTouchPoints || 0) > 0,
        is_brave: !!(navigator.brave && typeof navigator.brave.isBrave === 'function'),
    };
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  // If date is YYYY-MM-DD (no time/timezone), parse as local to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [y, m, d] = dateString.split('-');
    return `${d}/${m}/${y}`;
  }
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getStatusLabel(status) {
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    shipped: 'Expédiée',
    partially_received: 'Partiellement reçue',
    delivered: 'Livrée',
    cancelled: 'Annulée',
  };
  return labels[status] || status;
}

export function getStatusClass(status) {
  const classes = {
    pending: 'status-pending',
    confirmed: 'status-confirmed',
    shipped: 'status-shipped',
    partially_received: 'status-partially-received',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled',
  };
  return classes[status] || '';
}

export function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function getPeriodOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

export function getInventoryTypeLabel(type) {
  const labels = {
    initial: 'Stock Initial',
    intermediate: 'Inventaire Intermédiaire',
    final: 'Inventaire Final',
  };
  return labels[type] || type;
}

export function getMovementTypeLabel(type) {
  const labels = {
    reception: 'Réception',
    manual_reception: 'Réception',
    transfer_in: 'Transfert entrant',
    transfer_out: 'Transfert sortant',
    inventory_initial: 'Stock Initial',
    inventory_intermediate: 'Inventaire Intermédiaire',
    inventory_final: 'Inventaire Final',
  };
  return labels[type] || type;
}

/**
 * Normalize a string by removing accents (é, è, ê -> e, etc.)
 * Useful for accent-insensitive search
 */
export function normalizeString(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Check if a string matches a search term (accent-insensitive).
 * Multi-word support: each typed word (abbreviation) must appear somewhere,
 * e.g. "vin riz" matches "Vinaigre de riz JS-Alpha".
 */
export function matchesSearch(text, searchTerm) {
  if (!searchTerm) return true;
  if (!text) return false;
  const haystack = normalizeString(text);
  return normalizeString(searchTerm)
    .split(/\s+/)
    .filter(Boolean)
    .every(word => haystack.includes(word));
}
